<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import AIcon from '@/components/AIcon.vue'
import { useRoute, useRouter } from 'vue-router'
import useRouteHelper from '@/hooks/useRouteHelper'

const router = useRouter()
const route = useRoute()
const { useFindRoute } = useRouteHelper()
const settingsExpanded = ref(false)

const collectRouteNames = (routeRecord: Any): string[] => [
  ...(typeof routeRecord.name === 'string' ? [routeRecord.name] : []),
  ...(routeRecord.children ?? []).flatMap(collectRouteNames),
]

const props = withDefaults(
  defineProps<{
    collapsed?: boolean
  }>(),
  {
    collapsed: false,
  },
)

const items = computed(
  () =>
    useFindRoute('home')?.children.reduce((acc: Any[], child) => {
      if (!child.meta?.hidden) {
        const name = child.name ?? child.children?.find((route) => !route.meta?.hidden)?.name

        acc.push({
          meta: child.meta,
          name,
          route: child.path,
          routeNames: collectRouteNames(child),
          icon: child.meta?.icon,
          label: child.meta?.title,
          children:
            child.name === 'settings'
              ? (child.children ?? [])
                  .filter((route) => !route.meta?.hidden)
                  .map((route) => ({
                    name: route.name,
                    label: route.meta?.title,
                  }))
              : [],
        })
      }

      return acc
    }, []) || [],
)

const onJump = (item: Any) => {
  const name = item.name === 'home-index' ? 'dashboard' : item.name

  return router.push({ name })
}
const onTopLevelClick = (item: Any) => {
  if (item.name !== 'settings' || props.collapsed) {
    return onJump(item)
  }

  settingsExpanded.value = !settingsExpanded.value
}
const isRouteIncluded = (name: string) => route.matched.some((item) => item.name === name)
const isTopLevelRouteActive = (item: Any) =>
  item.routeNames.some((name: string) => isRouteIncluded(name))

watch(
  () => isRouteIncluded('settings'),
  (isSettingsRoute) => {
    if (isSettingsRoute) {
      settingsExpanded.value = true
    }
  },
  { immediate: true },
)
</script>

<template>
  <nav
    class="sidebar-navigation grid gap-2"
    :class="{ 'is-collapsed': props.collapsed }"
    aria-label="Primary navigation"
  >
    <template v-for="item in items" :key="item.name">
      <button
        type="button"
        class="sidebar-link"
        :class="{ 'is-active': isTopLevelRouteActive(item) }"
        :aria-current="isTopLevelRouteActive(item) ? 'page' : undefined"
        :aria-label="props.collapsed ? item.label : undefined"
        :title="props.collapsed ? item.label : undefined"
        :aria-expanded="item.name === 'settings' && !props.collapsed ? settingsExpanded : undefined"
        :aria-controls="
          item.name === 'settings' && !props.collapsed ? 'settings-submenu' : undefined
        "
        @click="onTopLevelClick(item)"
      >
        <AIcon class="sidebar-link__icon" :size="16" :icon="item.icon as Any" />
        <span v-if="!props.collapsed">{{ item.label }}</span>
        <i
          v-if="item.name === 'settings' && !props.collapsed"
          class="sidebar-link__toggle pi pi-chevron-down"
          :class="{ 'is-expanded': settingsExpanded }"
          aria-hidden="true"
        />
      </button>

      <div
        v-if="item.name === 'settings' && !props.collapsed && settingsExpanded"
        id="settings-submenu"
        class="settings-submenu"
      >
        <div class="settings-submenu__inner">
          <button
            v-for="child in item.children"
            :key="child.name"
            type="button"
            class="sidebar-sublink"
            :class="{ 'is-active': isRouteIncluded(child.name as string) }"
            @click="onJump(child)"
          >
            <span>{{ child.label }}</span>
          </button>
        </div>
      </div>
    </template>
  </nav>
</template>

<style scoped>
.sidebar-link {
  display: flex;
  min-height: 44px;
  width: 100%;
  align-items: center;
  gap: 10px;
  border: 0;
  border-radius: 8px;
  padding: 0 14px;
  background: transparent;
  color: var(--c-sidebar-text);
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition:
    background-color 0.18s ease,
    color 0.18s ease;
}

.sidebar-link:hover {
  background: var(--c-sidebar-hover);
  color: var(--c-sidebar-text);
}

.sidebar-link__icon {
  flex: 0 0 auto;
  color: var(--c-sidebar-icon);
}

.sidebar-link__toggle {
  margin-left: auto;
  color: var(--c-sidebar-icon);
  font-size: 10px;
  transition: transform var(--c-motion-fast) ease;
}

.sidebar-link__toggle.is-expanded {
  transform: rotate(180deg);
}

.sidebar-link.is-active {
  background: var(--c-sidebar-active);
  color: var(--c-sidebar-active-text);
  font-weight: 600;
}

.sidebar-link.is-active .sidebar-link__icon {
  color: var(--c-sidebar-active-text);
}

.settings-submenu__inner {
  min-height: 0;
  overflow: hidden;
}

.sidebar-sublink {
  display: flex;
  width: calc(100% - 14px);
  min-height: 36px;
  align-items: center;
  margin-left: 14px;
  border: 0;
  border-left: 1px solid var(--c-border);
  padding: 0 10px 0 16px;
  background: transparent;
  color: var(--c-sidebar-text);
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition:
    border-color var(--c-motion-fast) ease,
    color var(--c-motion-fast) ease;
}

.sidebar-sublink:hover {
  border-left-color: var(--p-surface-300);
  color: var(--c-sidebar-active-text);
}

.sidebar-sublink.is-active {
  border-left-color: var(--p-primary-500);
  color: var(--c-sidebar-active-text);
  font-weight: 600;
}

.sidebar-navigation.is-collapsed .sidebar-link {
  justify-content: center;
  padding: 0;
  color: var(--c-sidebar-text);
}

.sidebar-navigation.is-collapsed .sidebar-link:hover {
  background: var(--c-sidebar-hover);
  color: var(--c-sidebar-text);
}

.sidebar-navigation.is-collapsed .sidebar-link.is-active {
  background: var(--c-sidebar-active);
  color: var(--c-sidebar-active-text);
}
</style>
